import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, MenuItem, Chip, Grid,
  CircularProgress, Alert, Snackbar, Tooltip, IconButton, Divider, Tabs, Tab,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { useNavigate, useParams } from 'react-router-dom';
import {
  doc, getDoc, getDocs, updateDoc, addDoc, collection,
  serverTimestamp, query, where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { generateLabReport } from '../services/reportGenerator';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { isTextParam, isBinaryParam, parseRefBound, evaluateParameter, getRefDisplay } from '../utils/parameterEvaluator';

import { COLORS } from '../theme/constants';

const { primary: LAB_PRIMARY, teal: LAB_TEAL } = COLORS;

const PARAM_STATUS = {
  normal:   { label: 'Normal',     color: '#16A34A', bg: '#DCFCE7', icon: '🟢' },
  high:     { label: 'Alto ↑',    color: '#DC2626', bg: '#FEE2E2', icon: '🔴' },
  low:      { label: 'Bajo ↓',    color: '#2563EB', bg: '#DBEAFE', icon: '🔵' },
  critical: { label: '⚠ Crítico', color: '#7F1D1D', bg: '#FEE2E2', icon: '⚠️' },
  unknown:  { label: 'Sin ref.',   color: '#6B7280', bg: '#F3F4F6', icon: '⚪' },
  text:     { label: 'Descriptivo',color: '#6D28D9', bg: '#EDE9FE', icon: '📝' },
  positive: { label: 'Positivo +', color: '#C62828', bg: '#FCEBEA', icon: '+' },
  negative: { label: 'Negativo −', color: '#16A34A', bg: '#DCFCE7', icon: '−' },
};

const SPECIES_ALIAS = {
  canino: 'perro', felino: 'gato', equino: 'equino', bovino: 'bovino', aviar: 'aviar',
};

const norm = (s) =>
  (s || '').toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim();

const recalcStatus = (p) => evaluateParameter(p.value, p.ref, p.refType);

const buildMatchedParams = (template, allReadings, speciesKey) =>
  (template.parameters || []).map((tp, i) => {
    const originKey = (tp.originName || '').trim().toLowerCase();
    const n2 = norm(tp.name);
    const reading = allReadings.find((r) => {
      const raw = (r.paramName || '').trim().toLowerCase();
      if (originKey && raw === originKey) return true;
      const n1 = norm(r.paramName || '');
      return n1 === n2 || (n1.length > 3 && n2.length > 3 && (n1.includes(n2) || n2.includes(n1)));
    });
    const isText = isTextParam(tp);
    const isBinary = isBinaryParam(tp);
    const refType = isText ? 'none' : (tp.refType || 'minmax');
    const ref = isText ? null : (tp.refRanges?.[speciesKey] || tp.refRanges?.default || null);
    const value = reading?.value || (isText ? (tp.defaultText || '') : '');
    return {
      _idx: i,
      paramName: tp.name,
      value,
      unit: isText ? '' : (reading?.unit || tp.unit || ''),
      metodo: tp.metodo || '',
      ref,
      refType,
      refDisplay: isText ? '—' : getRefDisplay(ref, refType),
      category: tp.category || 'General',
      isText,
      isBinary,
      status: isText ? 'text' : recalcStatus({ value, ref, refType }),
      unmatched: isText ? false : !reading,
    };
  });

export default function GenerateReport() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const { activeBranch, activeBranchId } = useBranch();

  const [order, setOrder]               = useState(null);
  const [patient, setPatient]           = useState(null);
  const [owner, setOwner]               = useState(null);
  const [vet, setVet]                   = useState(null);
  const [allReadings, setAllReadings]   = useState([]);
  const [examTemplates, setExamTemplates] = useState({});
  const [matchedByExam, setMatchedByExam] = useState({});
  const [observations, setObservations] = useState('');
  const [activeTab, setActiveTab]       = useState(0);
  const [editingRow, setEditingRow]     = useState(null);
  const [loading, setLoading]           = useState(true);
  const [generating, setGenerating]     = useState(false);
  const [savingDraft, setSavingDraft]   = useState(false);
  const [draftsByExam, setDraftsByExam] = useState({});
  const [snackbar, setSnackbar]         = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const load = async () => {
      try {
        const orderDoc = await getDoc(doc(db, 'labOrders', orderId));
        if (!orderDoc.exists()) { setLoading(false); return; }
        const orderData = { id: orderDoc.id, ...orderDoc.data() };
        setOrder(orderData);

        const readingsSnap = await getDocs(
          query(collection(db, 'labReadings'), where('orderId', '==', orderId))
        );
        const readings = readingsSnap.docs.flatMap((d) => d.data().parameters || []);
        setAllReadings(readings);

        const exams = orderData.examsRequested || [];
        const templateDocs = await Promise.all(
          exams.map((e) => getDoc(doc(db, 'examTypes', e.examTypeId)))
        );
        const templates = {};
        templateDocs.forEach((d) => { if (d.exists()) templates[d.id] = { id: d.id, ...d.data() }; });
        setExamTemplates(templates);

        const species = (orderData.patientSpecies || 'perro').toLowerCase();
        const speciesKey = SPECIES_ALIAS[species] || species;

        const existingSnap = await getDocs(
          query(collection(db, 'labResults'), where('orderId', '==', orderId))
        );
        const draftsByExamTmp = {};
        existingSnap.docs.forEach((d) => {
          const data = d.data();
          if (data.status === 'draft' && data.examTypeId) {
            draftsByExamTmp[data.examTypeId] = { id: d.id, ...data };
          }
        });
        setDraftsByExam(draftsByExamTmp);

        const matched = {};
        exams.forEach((exam) => {
          const tpl = templates[exam.examTypeId];
          if (!tpl) return;
          const rows = buildMatchedParams(tpl, readings, speciesKey);
          const draft = draftsByExamTmp[exam.examTypeId];
          if (draft?.extractedParameters?.length) {
            const draftMap = {};
            draft.extractedParameters.forEach((dp) => {
              draftMap[dp._idx] = dp;
            });
            matched[exam.examTypeId] = rows.map((p) => {
              const d = draftMap[p._idx];
              if (!d) return p;
              const updated = { ...p, value: d.value !== undefined ? d.value : p.value, unit: d.unit !== undefined ? d.unit : p.unit };
              if (!p.isText) updated.status = recalcStatus(updated);
              return updated;
            });
          } else {
            matched[exam.examTypeId] = rows;
          }
        });
        setMatchedByExam(matched);

        const [patDoc] = await Promise.all([
          orderData.patientId ? getDoc(doc(db, 'patients', orderData.patientId)) : Promise.resolve(null),
        ]);
        if (patDoc?.exists()) {
          const p = { id: patDoc.id, ...patDoc.data() };
          setPatient(p);
          if (p.ownerId) {
            const ownerDoc = await getDoc(doc(db, 'owners', p.ownerId));
            if (ownerDoc.exists()) setOwner({ id: ownerDoc.id, ...ownerDoc.data() });
          }
        }
        if (orderData.vetId) {
          const vetDoc = await getDoc(doc(db, 'professionals', orderData.vetId));
          if (vetDoc.exists()) setVet({ id: vetDoc.id, ...vetDoc.data() });
        }

        const firstExamTypeId = exams[0]?.examTypeId;
        if (draftsByExamTmp[firstExamTypeId]) {
          setObservations(draftsByExamTmp[firstExamTypeId].observations || '');
        } else if (!existingSnap.empty) {
          setObservations(existingSnap.docs[0].data().observations || '');
        }
      } catch (err) {
        console.error('[GenerateReport] load error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [orderId]);

  const handleValueChange = useCallback((examTypeId, idx, field, value) => {
    setMatchedByExam((prev) => ({
      ...prev,
      [examTypeId]: prev[examTypeId].map((p) => {
        if (p._idx !== idx) return p;
        const updated = { ...p, [field]: value };
        if (field === 'value' && !p.isText) updated.status = recalcStatus(updated);
        return updated;
      }),
    }));
  }, []);

  const handleGenerate = async (examTypeId) => {
    setGenerating(true);
    try {
      const exam = order.examsRequested.find((e) => e.examTypeId === examTypeId);
      const params = matchedByExam[examTypeId] || [];
      const reviewerName = userData?.name || currentUser?.email || 'Técnico';

      const resultForPdf = {
        examTypeId,
        examTypeName: exam?.examTypeName || '',
        examCategory: examTemplates[examTypeId]?.category || '',
        tipoMuestra: examTemplates[examTypeId]?.tipoMuestra || '',
        patientName: order.patientName,
        patientSpecies: order.patientSpecies,
        extractedParameters: params,
        observations,
        reviewedByName: reviewerName,
        reportGeneratedAt: new Date(),
        status: 'reported',
      };

      const blob = await generateLabReport({ order, result: resultForPdf, patient, owner, vet, branch: activeBranch });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `informe-${order.orderNumber}-${exam?.examTypeName || examTypeId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);

      const now = serverTimestamp();

      await updateDoc(doc(db, 'labOrders', orderId), {
        [`examResults.${examTypeId}.status`]: 'reported',
        status: 'reported',
      });

      const existingResults = await getDocs(
        query(collection(db, 'labResults'), where('orderId', '==', orderId), where('examTypeId', '==', examTypeId))
      );
      const reportPayload = {
        orderId,
        examTypeId,
        examTypeName: exam?.examTypeName || '',
        patientId: order.patientId || '',
        patientName: order.patientName || '',
        patientSpecies: order.patientSpecies || '',
        extractedParameters: params,
        observations,
        status: 'reported',
        reviewedBy: currentUser?.uid || '',
        reviewedByName: reviewerName,
        reviewedAt: now,
        reportGeneratedAt: now,
        branchId: order.branchId || activeBranchId || '',
      };
      if (!existingResults.empty) {
        await updateDoc(doc(db, 'labResults', existingResults.docs[0].id), reportPayload);
      } else {
        await addDoc(collection(db, 'labResults'), reportPayload);
      }

      await addDoc(collection(db, 'labAuditLog'), {
        orderId,
        examTypeId,
        action: 'report_generated',
        performedBy: currentUser?.uid || '',
        performedAt: now,
        details: `Informe generado. ${params.length} parámetros del catálogo. Revisado por: ${reviewerName}`,
      });

      setSnackbar({ open: true, message: `Informe de ${exam?.examTypeName} generado y descargado.`, severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: `Error: ${err.message}`, severity: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveDraft = async (examTypeId) => {
    setSavingDraft(true);
    try {
      const exam = order.examsRequested.find((e) => e.examTypeId === examTypeId);
      const params = matchedByExam[examTypeId] || [];
      const now = serverTimestamp();
      const reviewerName = userData?.name || currentUser?.email || 'Técnico';
      const payload = {
        orderId,
        examTypeId,
        examTypeName: exam?.examTypeName || '',
        patientId: order.patientId || '',
        patientName: order.patientName || '',
        patientSpecies: order.patientSpecies || '',
        extractedParameters: params,
        observations,
        status: 'draft',
        reviewedBy: currentUser?.uid || '',
        reviewedByName: reviewerName,
        reviewedAt: now,
        branchId: order.branchId || activeBranchId || '',
      };

      const existingSnap = await getDocs(
        query(collection(db, 'labResults'), where('orderId', '==', orderId))
      );
      const existingDraft = existingSnap.docs.find(
        (d) => d.data().examTypeId === examTypeId && d.data().status === 'draft'
      );

      if (existingDraft) {
        await updateDoc(doc(db, 'labResults', existingDraft.id), payload);
      } else {
        await addDoc(collection(db, 'labResults'), payload);
      }

      setSnackbar({ open: true, message: `Borrador de ${exam?.examTypeName} guardado.`, severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: `Error al guardar borrador: ${err.message}`, severity: 'error' });
    } finally {
      setSavingDraft(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!order) return <Alert severity="error" sx={{ m: 3 }}>Orden no encontrada.</Alert>;

  const exams = order.examsRequested || [];
  const activeExam = exams[activeTab];
  const activeParams = activeExam ? (matchedByExam[activeExam.examTypeId] || []) : [];
  const hasReadings = allReadings.length > 0;

  const grouped = activeParams.reduce((acc, p) => {
    const cat = p.category || 'General';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(p);
    return acc;
  }, {});

  const criticalCount = activeParams.filter((p) => p.status === 'critical').length;
  const unmatchedCount = activeParams.filter((p) => p.unmatched).length;
  const isReported = order.examResults?.[activeExam?.examTypeId]?.status === 'reported';

  return (
    <Box sx={{ bgcolor: '#F4F6F8', minHeight: '100vh', p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/lab/orders')}
            sx={{ color: LAB_PRIMARY, fontWeight: 600 }}>Volver</Button>
          <Typography variant="h5" fontWeight={700} color={LAB_PRIMARY}>
            Generar Informe
          </Typography>
          <Chip label={order.orderNumber} size="small"
            sx={{ bgcolor: '#EFF6FF', color: LAB_PRIMARY, fontWeight: 700 }} />
        </Box>
        {activeExam && hasReadings && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={savingDraft ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={() => handleSaveDraft(activeExam.examTypeId)}
              disabled={savingDraft}
              sx={{ color: LAB_PRIMARY, borderColor: LAB_PRIMARY, fontWeight: 700 }}>
              {savingDraft ? 'Guardando…' : 'Guardar borrador'}
            </Button>
            <Button
              variant="contained"
              startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
              onClick={() => handleGenerate(activeExam.examTypeId)}
              disabled={generating}
              sx={{ bgcolor: LAB_TEAL, fontWeight: 700, '&:hover': { bgcolor: '#229984' } }}>
              {generating ? 'Generando…' : `Generar — ${activeExam.examTypeName}`}
            </Button>
          </Box>
        )}
      </Box>
      {/* Patient info */}
      <Paper elevation={0} sx={{ p: 1.5, mb: 2, borderRadius: 2, border: '1px solid #BAE6FD', bgcolor: '#F0F9FF' }}>
        <Grid container alignItems="center" spacing={1}>
          <Grid size="grow">
            <Typography variant="body2" fontWeight={700} color={LAB_PRIMARY}>
              {order.patientName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {order.patientSpecies} · Propietario: {order.ownerName || '—'} · MV: {order.vetName || '—'}
            </Typography>
          </Grid>
          <Grid>
            <Chip
              label={hasReadings ? `${allReadings.length} parámetros leídos` : 'Sin lecturas cargadas'}
              size="small"
              sx={{
                bgcolor: hasReadings ? '#DCFCE7' : '#FEF3C7',
                color:   hasReadings ? '#16A34A' : '#D97706',
                fontWeight: 700,
              }}
            />
          </Grid>
        </Grid>
      </Paper>
      {!hasReadings && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          No hay parámetros cargados para esta orden. Procesa un resultado de examen primero para que el sistema lea los valores.
        </Alert>
      )}
      {isReported && (
        <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2, borderRadius: 2 }}>
          Ya se generó un informe para este examen.
        </Alert>
      )}
      {/* Exam tabs */}
      {exams.length > 1 && (
        <Paper elevation={0} sx={{ borderRadius: 2, mb: 2, border: '1px solid #E2E8F0' }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => { setActiveTab(v); setEditingRow(null); }}
            sx={{ '& .MuiTab-root': { fontWeight: 700, fontSize: '0.82rem' } }}>
            {exams.map((exam) => {
              const st = order.examResults?.[exam.examTypeId]?.status;
              return (
                <Tab key={exam.examTypeId} label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {exam.examTypeName}
                    {st === 'reported' && <CheckCircleIcon sx={{ fontSize: 14, color: LAB_TEAL }} />}
                  </Box>
                } />
              );
            })}
          </Tabs>
        </Paper>
      )}
      {/* Alerts */}
      {criticalCount > 0 && (
        <Alert severity="error" icon={<WarningAmberIcon />} sx={{ mb: 1.5, borderRadius: 2 }}>
          <strong>{criticalCount} parámetro(s) con valor CRÍTICO</strong> — Revisar antes de generar informe.
        </Alert>
      )}
      {hasReadings && unmatchedCount > 0 && (
        <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 2 }}>
          {unmatchedCount} parámetro(s) del catálogo sin lectura — aparecerán en blanco en el informe.
        </Alert>
      )}
      {/* Parameters table */}
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #E2E8F0' }}>
        {activeExam && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} color={LAB_PRIMARY}>
              {activeExam.examTypeName}
            </Typography>
            {examTemplates[activeExam.examTypeId] && (
              <Chip
                label={`${(examTemplates[activeExam.examTypeId].parameters || []).length} parámetros en plantilla`}
                size="small"
                sx={{ bgcolor: '#EFF6FF', color: LAB_PRIMARY, fontSize: '0.7rem' }}
              />
            )}
          </Box>
        )}

        {Object.entries(grouped).map(([category, params]) => (
          <Box key={category} sx={{ mb: 2 }}>
            <Box sx={{ bgcolor: LAB_TEAL, px: 2, py: 0.5, borderRadius: '8px 8px 0 0' }}>
              <Typography variant="caption" fontWeight={700} color="#fff"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
                {category}
              </Typography>
            </Box>
            <TableContainer sx={{ border: '1px solid #E2E8F0', borderTop: 'none', borderRadius: '0 0 8px 8px' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: LAB_PRIMARY }}>Parámetro</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: LAB_PRIMARY, width: 90 }}>Valor</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: LAB_PRIMARY, width: 70 }}>Unidad</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: LAB_PRIMARY, width: 120 }}>Referencia</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: LAB_PRIMARY, width: 90 }}>Estado</TableCell>
                    <TableCell sx={{ width: 40 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {params.map((p) => {
                    const st = PARAM_STATUS[p.status] || PARAM_STATUS.unknown;
                    const isEditing =
                      editingRow?.examTypeId === activeExam?.examTypeId && editingRow?.idx === p._idx;
                    const isCritical = p.status === 'critical';
                    const isAbnormal = ['high', 'low', 'critical', 'positive'].includes(p.status);
                    return (
                      <TableRow key={p._idx}
                        sx={{
                          bgcolor: isCritical ? '#FFF5F5' : p.unmatched ? '#FFFBEB' : isAbnormal ? '#FFF5F5' : 'inherit',
                          '&:hover': { bgcolor: '#F8FAFC' },
                        }}>
                        <TableCell sx={{
                          fontSize: '0.78rem',
                          fontWeight: p.unmatched ? 400 : 600,
                          fontStyle: p.unmatched ? 'italic' : 'normal',
                          color: isCritical ? '#991B1B' : 'inherit',
                        }}>
                          {p.paramName}
                          {p.unmatched && (
                            <Chip label="Sin lectura" size="small"
                              sx={{ ml: 0.5, fontSize: '0.58rem', height: 15, bgcolor: '#FEF3C7', color: '#92400E' }} />
                          )}
                        </TableCell>
                        {p.isText ? (
                          <TableCell colSpan={3}>
                            {isEditing ? (
                              <TextField fullWidth size="small" multiline minRows={2} value={p.value}
                                onChange={(e) =>
                                  handleValueChange(activeExam.examTypeId, p._idx, 'value', e.target.value)}
                                placeholder="Describe la morfología observada…"
                                inputProps={{ style: { fontSize: '0.78rem' } }} autoFocus />
                            ) : (
                              <Typography variant="body2" sx={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                                {p.value || '—'}
                              </Typography>
                            )}
                          </TableCell>
                        ) : p.isBinary ? (
                          <>
                            <TableCell>
                              {isEditing ? (
                                <TextField select size="small" value={p.value}
                                  onChange={(e) =>
                                    handleValueChange(activeExam.examTypeId, p._idx, 'value', e.target.value)}
                                  inputProps={{ style: { fontSize: '0.78rem', padding: '3px 6px' } }}
                                  sx={{ width: 100 }} autoFocus>
                                  <MenuItem value=""><em>—</em></MenuItem>
                                  <MenuItem value="Negativo">Negativo</MenuItem>
                                  <MenuItem value="Positivo">Positivo</MenuItem>
                                </TextField>
                              ) : (
                                <Typography variant="body2" fontWeight={700}
                                  color={p.status === 'positive' ? st.color : 'inherit'}
                                  sx={{ fontSize: '0.82rem' }}>
                                  {p.value || '—'}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{p.unit || '—'}</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{p.refDisplay}</TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell>
                              {isEditing ? (
                                <TextField size="small" value={p.value}
                                  onChange={(e) =>
                                    handleValueChange(activeExam.examTypeId, p._idx, 'value', e.target.value)}
                                  inputProps={{ style: { fontSize: '0.78rem', padding: '3px 6px' } }}
                                  sx={{ width: 80 }} autoFocus />
                              ) : (
                                <Typography variant="body2" fontWeight={700}
                                  color={['high', 'low', 'critical', 'positive'].includes(p.status) ? st.color : 'inherit'}
                                  sx={{ fontSize: '0.82rem' }}>
                                  {p.value || '—'}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                              {isEditing ? (
                                <TextField size="small" value={p.unit}
                                  onChange={(e) =>
                                    handleValueChange(activeExam.examTypeId, p._idx, 'unit', e.target.value)}
                                  inputProps={{ style: { fontSize: '0.75rem', padding: '3px 4px' } }}
                                  sx={{ width: 65 }} />
                              ) : p.unit || '—'}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{p.refDisplay}</TableCell>
                          </>
                        )}
                        <TableCell>
                          <Chip label={`${st.icon} ${st.label}`} size="small"
                            sx={{ bgcolor: st.bg, color: st.color, fontWeight: 700, fontSize: '0.65rem', height: 20 }} />
                        </TableCell>
                        <TableCell>
                          <Tooltip title={isEditing ? 'Guardar' : 'Editar valor'}>
                            <IconButton size="small"
                              onClick={() =>
                                setEditingRow(
                                  isEditing ? null : { examTypeId: activeExam.examTypeId, idx: p._idx }
                                )
                              }
                              sx={{ color: isEditing ? LAB_TEAL : LAB_PRIMARY }}>
                              {isEditing
                                ? <SaveIcon sx={{ fontSize: 16 }} />
                                : <EditIcon sx={{ fontSize: 16 }} />}
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))}

        {activeParams.length === 0 && (
          <Alert severity="info">
            {hasReadings
              ? 'No se encontraron coincidencias entre los parámetros leídos y la plantilla de este examen.'
              : 'Carga los resultados del examen primero usando "Subir resultado de examen".'}
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" fontWeight={700} color={LAB_PRIMARY} mb={1}>
          Observaciones / Interpretación clínica
        </Typography>
        <TextField
          fullWidth multiline rows={3} size="small"
          placeholder="Observaciones clínicas, interpretación o comentarios adicionales…"
          value={observations}
          onChange={(e) => setObservations(e.target.value)}
        />
      </Paper>
      <Snackbar open={snackbar.open} autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
